import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../providers/AuthContext";
import { useAuthApi } from "../../../api/authApi";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../../utils/errorHandler";

export type SignInFormValues = {
  email: string;
  password: string;
  rememberMe?: boolean;
};

export const useSignIn = () => {
  const [showResend, setShowResend] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { login } = useAuth();
  const { loginUser, resendVerification } = useAuthApi();

  const form = useForm<SignInFormValues>({
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  const {
    register,
    handleSubmit,
    setError,
    getValues,
    formState: { errors, isSubmitting },
  } = form;

  const onSubmit = async (data: SignInFormValues) => {
    try {
      const { token, emailNotVerified } = await loginUser(
        data.email,
        data.password,
        true,
      );

      if (emailNotVerified) {
        setShowResend(true);
        setError("root", { message: "Email not verified" });
        return;
      }

      if (token) {
        await login(token);

        const redirectTo = searchParams.get("redirect");
        navigate(redirectTo || "/profile");
      } else {
        setError("root", { message: "No token received" });
        toast.error("No token received");
      }
    } catch (err: any) {
      setError("root", { message: err.message || "Login failed" });
    }
  };

  const handleForgotPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate("/forgot-password");
  };

  const handleResendVerification = async () => {
    const email = getValues("email");
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    try {
      await resendVerification(email);
      toast.success("Verification email sent. Check your inbox (and spam).");
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, "Failed to resend"));
    }
  };

  return {
    // Form
    register,
    handleSubmit,
    errors,
    isSubmitting,
    onSubmit,

    // UI State
    showResend,

    // Actions
    handleForgotPassword,
    handleResendVerification,
  };
};
